import { min } from 'lodash/fp';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';

import { useAppSelector } from '../../../hooks/react-redux';
import { DATASET_HEIGHT_OFFSET, DATASET_INSTANCE_HEADER, PAGE_MARGIN_PADDING_BORDER, TIMELINE_WRAPPER, useSplitView } from '../../../hooks/useSplitView';
import { getFormInstanceIsScrolling } from '../../datasets/instances/store';
import { getDocumentAnalysisView } from '../../documents/my-documents/store';
import { ScrollableProps } from './Scrollable';
import styles from './Scrollable.module.scss';

interface TableInstanceScrollableProps extends ScrollableProps {
    collapsedRows: string[];
}

const MENU_WRAPPER = 90;
const SCROLLBAR_HEIGHT_OFFSET = 6;
const INSTANCE_WRAPPER_TOP_OFFSET = (PAGE_MARGIN_PADDING_BORDER / 2) + TIMELINE_WRAPPER + DATASET_INSTANCE_HEADER + MENU_WRAPPER;

export const TableInstanceScrollable: React.FC<TableInstanceScrollableProps> = ({ children, height = '100%', maxHeight = '100%', width = '100%', autoHeight = true, paddingBottom = '4px', collapsedRows }) => {
    const scrollRef = useRef<Scrollbars>(null);
    const [tableTop, setTableTop] = useState<number>(0);
    const tableWrapperRef = useRef<HTMLDivElement>(null);

    const [scrollHeight, setScrollHeight] = useState<number>(0);

    const documentAnalysisView = useAppSelector(getDocumentAnalysisView);
    const formInstanceIsScrolling = useAppSelector(getFormInstanceIsScrolling);
    const widthOffset = PAGE_MARGIN_PADDING_BORDER;
    const heightOffset = DATASET_HEIGHT_OFFSET;
    const [, instanceHeight] = useSplitView(documentAnalysisView, widthOffset, heightOffset);

    const calculateScrollHeight = useCallback(() => {
        if (scrollRef.current) {
            const scrollable = scrollRef.current;
            const scrollHeight = scrollable.getScrollHeight();
            setScrollHeight(scrollHeight);
        }
    }, []);

    const calculateTop = useCallback(() => {
        if (tableWrapperRef.current) {
            const yPosition = tableWrapperRef.current.getBoundingClientRect().y;
            const top = yPosition;
            setTableTop(top);
        }
    }, []);

    const relativeTop = useMemo(() => tableTop - INSTANCE_WRAPPER_TOP_OFFSET, [tableTop]);
    const gapToBottom = useMemo(() => instanceHeight - relativeTop, [instanceHeight, relativeTop]);
    const scrollTop = useMemo(() => min([gapToBottom, scrollHeight - SCROLLBAR_HEIGHT_OFFSET]), [scrollHeight, gapToBottom]);

    const recalculateScrollPosition = useCallback(() => {
        calculateTop();
        calculateScrollHeight();
    }, [calculateTop, calculateScrollHeight]);

    useEffect(() => {
        recalculateScrollPosition();
        window.addEventListener('scroll', () => recalculateScrollPosition());
        window.addEventListener('wheel', () => recalculateScrollPosition());
        window.addEventListener('resize', () => calculateTop());
        return () => {
            window.removeEventListener('scroll', () => recalculateScrollPosition());
            window.removeEventListener('wheel', () => recalculateScrollPosition());
            window.removeEventListener('resize', () => calculateTop());
        };
    }, [recalculateScrollPosition, calculateTop]);

    useEffect(() => {
        recalculateScrollPosition();
    }, [collapsedRows, recalculateScrollPosition]);

    useEffect(() => {
        if (!formInstanceIsScrolling) {
            setTimeout(recalculateScrollPosition, 200);
        }
    }, [formInstanceIsScrolling, recalculateScrollPosition]);

    /* eslint-disable @typescript-eslint/no-explicit-any */
    const renderTrackHorizontal = useCallback((props: any) => <div {...props} style={{ ...props.style, top: `${scrollTop}px`, width: '100%', borderRadius: '3px' }} />, [scrollTop]);

    return (
        <div style={{ display: 'flex', height, width }} ref={tableWrapperRef}>
            <Scrollbars
                style={{ width, height, paddingBottom }}
                autoHeight={autoHeight}
                autoHeightMin={height}
                autoHeightMax={maxHeight}
                hideTracksWhenNotNeeded
                ref={scrollRef}
                renderTrackHorizontal={renderTrackHorizontal}
                renderThumbHorizontal={props => <div {...props} className={styles.thumb} />}
            >
                {children}
            </Scrollbars>
        </div>
    );
};
